import {
  FLUTTER_HANDLER_GET_USER_DATA,
  FLUTTER_HANDLER_PRESS_LOGOUT,
  FLUTTER_HANDLER_REQUEST_HEALTH,
} from "../integration/flutterWebViewBridge.contract";
import type { FlutterUserData } from "../types/flutterHealthBridge.types";

export const isGluceraFlutterApp = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.flutter_inappwebview?.callHandler === "function";

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Flutter bridge can inject slightly after page load — wait before giving up. */
export const waitForFlutterBridge = async (timeoutMs = 3000): Promise<boolean> => {
  if (isGluceraFlutterApp()) return true;

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await sleep(100);
    if (isGluceraFlutterApp()) return true;
  }

  return false;
};

export const parseFlutterHandlerResponse = <T>(raw: unknown): T | null => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw as T;
  return null;
};

/**
 * Web → Flutter: invoke the `getUserData` bridge handler for health data.
 * Only meant to be called from the Trends page (via `useGoogleHealthSync`).
 * Auth is handled separately via localStorage tokens in `useGluceraAuth`.
 */
export const getFlutterUserData = async (): Promise<FlutterUserData | null> => {
  if (!isGluceraFlutterApp()) return null;
  try {
    const raw = await window.flutter_inappwebview!.callHandler(
      FLUTTER_HANDLER_GET_USER_DATA,
    );
    return parseFlutterHandlerResponse<FlutterUserData>(raw);
  } catch {
    return null;
  }
};

/** Web → Flutter: ask app to clear WebView cookies/storage on logout. */
export const pressLogoutFromFlutter = async (): Promise<{
  ok: boolean;
  raw: unknown;
  skipped: boolean;
}> => {
  if (!isGluceraFlutterApp()) {
    return { ok: false, raw: null, skipped: true };
  }
  try {
    const raw = await window.flutter_inappwebview!.callHandler(
      FLUTTER_HANDLER_PRESS_LOGOUT,
    );
    return { ok: true, raw, skipped: false };
  } catch {
    return { ok: false, raw: null, skipped: false };
  }
};

/** Web → Flutter: ask app to read Google Health and call window.GluceraWeb.onHealthData. */
export const requestGoogleHealthDataFromFlutter = async (): Promise<{
  ok: boolean;
  raw: unknown;
}> => {
  if (!isGluceraFlutterApp()) return { ok: false, raw: null };
  try {
    const raw = await window.flutter_inappwebview!.callHandler(
      FLUTTER_HANDLER_REQUEST_HEALTH,
    );
    return { ok: true, raw };
  } catch {
    return { ok: false, raw: null };
  }
};
